import { ComponentRef, Directive, DoCheck, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { BrowserPaintService } from '../shared/services/browser-paint.service';
import { PageVisibilityService } from '../shared/services/page-visibility.service';
import { SchedulerService } from '../shared/services/scheduler.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { LazyLoadableModuleId } from './lazy-loadable-module-id.enum';
import { LazyViewLoaderService } from './lazy-view-loader.service';
import { ModulePriorityService } from './module-priority.service';

@Directive({
    selector: '[ohpLazyView]'
})
export class LazyViewDirective implements OnInit, DoCheck, OnDestroy {
    /**
     * This should be a constant string, set for usage in `ngOnInit()`.
     *
     * The value should come from the `bundle.*.js` file used to build our bundles.
     *
     * The value should be a key in `AngularCompilerPlugin` for `additionalLazyModules`,
     * appended with `#ModuleName`.
     *
     * For example:
     * `<ng-container ohpLazyView ngModulePath="feedback#FeedbackModule"></ng-container>`
     *
     * The `AngularCompilerPlugin.additionalLazyModules` looks like:
    ```js
        additionalLazyModules: {
            'feedback': 'absolute/path/to/module/file'
        }
    ```
     */
    @Input() public ngModulePath: LazyLoadableModuleId;

    /**
     * `@Input()`s for the dynamically created component.
     * For example, if the lazy component to load has an `Input() foo: string`,
     * I can set in the HTML:
     * `<ng-container ohpLazyView [inputs]="{ foo: 'bar' }"></ng-container>` and the lazy Component's
     * `foo` input will be the string `bar`.
     * NOTE: This will not fire `ngOnChanges()` in the lazy Component. Consider using a `set`
     * value instead if `ngOnChanges()` is needed.
     */
    @Input() public inputs: { [k: string]: any };

    @Input() public outputs: { [k: string]: Function };

    @Output('ohpLazyViewComponentLoaded') public readonly componentLoaded = new EventEmitter<ComponentRef<any>>();

    private lazyComponentRef: ComponentRef<any>;
    private readonly outputsSubscriptionMap: Map<string, Subscription> = new Map();
    private creationHandle: number;

    constructor(
        private readonly lazyViewLoaderService: LazyViewLoaderService,
        private readonly viewRef: ViewContainerRef,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly scheduler: SchedulerService,
        private readonly modulePriorityService: ModulePriorityService,
        private readonly browserPaintService: BrowserPaintService,
        private readonly ngZone: NgZone,
        private readonly pageVisibilityService: PageVisibilityService
    ) {
    }

    public async ngOnInit() {
        if (!this.ngModulePath) {
            return;
        }

        try {
            await this.loadComponent();
        } catch (ex) {
            this.globalErrorHandler.handleError(ex, ErrorId.ehPAl);
        }
    }

    public ngDoCheck() {
        this.setInputsOutputs();
    }

    public ngOnDestroy() {
        if (this.creationHandle) {
            this.browserPaintService.cancel(this.creationHandle);
        }

        this.outputsSubscriptionMap.forEach(subscription => subscription.unsubscribe());
        this.viewRef.clear();
    }

    // TODO: Investigate if we can make this more runtime efficient by using `KeyValueDiffers`.
    private setInputsOutputs() {
        if (!this.lazyComponentRef) {
            return;
        }

        for (const inputName in this.inputs) {
            const inputValue = this.inputs[inputName];
            this.lazyComponentRef.instance[inputName] = inputValue;
        }

        for (const outputName in this.outputs) {
            if (this.outputsSubscriptionMap.has(outputName)) {
                continue;
            }

            const lazyOutput: EventEmitter<any> = this.lazyComponentRef.instance[outputName];

            if (!lazyOutput || !(lazyOutput instanceof EventEmitter)) {
                continue;
            }

            const outputFunction = this.outputs[outputName];

            const onEmit = lazyOutput.subscribe(outputFunction);

            this.outputsSubscriptionMap.set(outputName, onEmit);
        }
    }

    private async loadComponent(): Promise<void> {
        const componentFactory = await this.lazyViewLoaderService.loadLazyViewFromModule(this.ngModulePath);

        // Instantiate the `@Component` in view for this `LazyViewComponent` and return the `ComponentRef`.
        const createComponent = () => {
            this.lazyComponentRef = this.viewRef.createComponent(componentFactory);
            this.componentLoaded.emit(this.lazyComponentRef);
        };

        // DOM updates should happen within `requestAnimationFrame` when page is visible
        // `requestAnimationFrame` calls are paused in most browsers when page is hidden or
        // running in the background tab to improve performance and battery life.
        // When page is hidden, skip scheduling and create component instantly.
        // This can help avoid cases where lazy loaded component creations is delayed for a long time
        // due to browser creating components in `requestAnimationFrame` and/or possible throttle of idle periods
        if (this.clientStartupConfigService.config.chunkLoadStrategy === 'idle-callback' && this.pageVisibilityService.isVisible()) {
            const priority = this.modulePriorityService.getSchedulePriority(this.ngModulePath);
            this.scheduler.scheduleWork(() => {
                this.ngZone.run(() => {
                    this.creationHandle = this.browserPaintService.runBeforeLayout(() => {
                        this.creationHandle = null;
                        createComponent();
                        this.setInputsOutputs();
                    });
                });
            }, priority);
        } else {
            createComponent();
        }
    }
}
